import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, X, Send, Bot, Loader2, Minimize2, Zap, Lock } from 'lucide-react';
import { toProfileSlug } from '../utils/slug';
import { useTheme } from '../contexts/ThemeContext';

const C = {
  bg: 'var(--pd-bg)', purple: 'var(--pd-purple)', cyan: 'var(--pd-cyan)',
  text: 'var(--pd-text)', text2: 'var(--pd-text2)', muted: 'var(--pd-muted)',
  border: 'var(--pd-border)', glass: 'var(--pd-glass)',
  gold: 'var(--pd-gold)', red: 'var(--pd-red)',
};

const TIER_LABELS = {
  free: 'Free', verified: 'Free',
  licensed: 'Licensed', featured: 'Featured', powerhouse: 'Powerhouse',
};

const FloatingCopilot = ({ showOnlyOnDashboard }) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [newsletterOpen, setNewsletterOpen] = useState(
    typeof window !== 'undefined' && window.__pdNewsletterOpen === true
  );
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open && user) fetchUsage();
  }, [open]);

  // Suppress chat widget while newsletter modal is open
  useEffect(() => {
    const onOpen = () => setNewsletterOpen(true);
    const onClose = () => setNewsletterOpen(false);
    window.addEventListener('pd-newsletter-open', onOpen);
    window.addEventListener('pd-newsletter-close', onClose);
    return () => {
      window.removeEventListener('pd-newsletter-open', onOpen);
      window.removeEventListener('pd-newsletter-close', onClose);
    };
  }, []);

  const fetchUsage = async () => {
    try {
      const { data } = await axios.get(`${API}/copilot/usage`);
      
      // Override backend logic since backend fix is not deployed yet
      const TIER_LIMITS = { free: 2, verified: 2, licensed: 10, featured: 20, powerhouse: -1 };
      
      // Try to get podcaster profile for true tier
      let actualTier = user?.subscription_tier || 'free';
      try {
        const { data: profile } = await axios.get(`${API}/podcasters/user/${user.id}`);
        if (profile && profile.subscription_tier) {
          actualTier = profile.subscription_tier;
        }
      } catch (e) {
        // Fallback to user tier
      }

      const limit = TIER_LIMITS[actualTier] ?? 2;
      const sessionSends = parseInt(sessionStorage.getItem('copilot_sends_today') || '0', 10);
      
      setUsage({
        ...data,
        limit,
        tier: actualTier,
        unlimited: limit === -1,
        remaining: limit === -1 ? -1 : Math.max(0, limit - (data.used || 0) - sessionSends)
      });
    } catch {
      setUsage(null);
    }
  };

  const isLimitReached = usage && !usage.unlimited && usage.remaining <= 0;

  // Only render on dashboard/copilot pages if restricted
  // Hide when user is already on the full copilot panel to avoid redundancy
  const isCopilotTab = location.pathname.startsWith('/dashboard') && new URLSearchParams(location.search).get('tab') === 'copilot';
  // Hide while newsletter modal is open (first-visit subscription gate)
  if (newsletterOpen) return null;
  if (isCopilotTab) return null;
  if (showOnlyOnDashboard && !location.pathname.startsWith('/dashboard') && !location.pathname.startsWith('/copilot')) {
    return null;
  }

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading || isLimitReached) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/copilot/chat`, {
        message: msg,
        conversation_id: conversationId,
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        suggestions: data.suggested_profiles || [],
      }]);
      if (data.conversation_id) setConversationId(data.conversation_id);
      
      // Local tracking workaround for rate limit
      const currentSends = parseInt(sessionStorage.getItem('copilot_sends_today') || '0', 10);
      sessionStorage.setItem('copilot_sends_today', currentSends + 1);
      
      // Refresh usage after successful message
      fetchUsage();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429) {
        // Rate limited
        setMessages(prev => [...prev, {
          role: 'system',
          content: typeof detail === 'object' ? detail.message : (detail || 'Daily limit reached. Upgrade your plan for more AI messages!'),
        }]);
        fetchUsage();
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Sorry, I had trouble processing that. Please try again.',
          suggestions: [],
        }]);
      }
    }
    setLoading(false);
  };

  if (!user) return null;

  const usageBadge = () => {
    if (!usage) return null;
    if (usage.unlimited) {
      return (
        <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(34,211,238,0.15)', color: C.cyan }}>
          <Zap className="w-2.5 h-2.5" /> Unlimited
        </span>
      );
    }
    const pct = usage.limit > 0 ? (usage.remaining / usage.limit) * 100 : 0;
    const color = pct > 50 ? '#22C55E' : pct > 20 ? C.gold : C.red;
    return (
      <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${color}15`, color }}>
        {usage.remaining}/{usage.limit} left
      </span>
    );
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="floating-copilot-btn"
          className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300  group"
          style={{
            background: '#8B5CF6',
            boxShadow: '0 0 30px rgba(139,92,246,0.4), 0 0 60px rgba(34,211,238,0.2)',
          }}
        >
          <MessageSquare className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-400 animate-pulse" style={{ borderWidth: 2, borderStyle: 'solid', borderColor: 'var(--pd-bg)' }} />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="floating-copilot-panel"
          className="fixed bottom-6 right-6 z-[9999] w-[380px] max-w-[calc(100vw-48px)] rounded-2xl overflow-hidden flex flex-col shadow-2xl"
          style={{
            height: '520px',
            maxHeight: 'calc(100vh - 120px)',
            background: isDark ? 'rgba(10,10,10,0.95)' : 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(24px)',
            border: isDark ? '1px solid rgba(139,92,246,0.2)' : '1px solid rgba(0,0,0,0.1)',
            boxShadow: isDark
              ? '0 0 40px rgba(139,92,246,0.15), 0 25px 50px rgba(0,0,0,0.5)'
              : '0 8px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: C.border, background: isDark ? 'rgba(139,92,246,0.06)' : 'rgba(139,92,246,0.04)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#8B5CF6' }}>
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold" style={{ color: C.text }}>Match Copilot</p>
                {usageBadge()}
              </div>
              <p className="text-[10px]" style={{ color: C.muted }}>AI matchmaking assistant</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg transition-colors" data-testid="floating-copilot-close">
                <X className="w-4 h-4" style={{ color: C.muted }} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && !isLimitReached && (
              <div className="flex flex-col items-center justify-center h-full gap-3 px-2">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: isDark ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.08)', border: `1px solid ${C.border}` }}>
                  <Bot className="w-6 h-6" style={{ color: C.purple }} />
                </div>
                <p className="text-xs text-center" style={{ color: C.text2 }}>
                  Ask me to find your ideal podcast swap partners!
                </p>
                <div className="grid grid-cols-1 gap-1.5 w-full">
                  {['Find me tech podcast hosts', 'Who matches my niche?', 'Best guests for my show'].map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q)}
                      data-testid={`floating-copilot-starter-${i}`}
                      className="p-2 rounded-lg border text-left text-xs transition-all hover:border-purple-500/30"
                      style={{ background: 'var(--pd-card-bg)', borderColor: C.border, color: C.text2 }}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#8B5CF6' }}>
                    <Bot className="w-3 h-3 text-white" />
                  </div>
                )}
                {msg.role === 'system' && (
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(245,158,11,0.2)' }}>
                    <Lock className="w-3 h-3" style={{ color: C.gold }} />
                  </div>
                )}
                <div className={`max-w-[80%] space-y-1.5 ${msg.role === 'user' ? 'order-1' : ''}`}>
                  <div className="p-2.5 rounded-xl text-xs leading-relaxed"
                    style={
                      msg.role === 'user'
                        ? { background: C.purple, color: '#fff', borderBottomRightRadius: 4 }
                        : msg.role === 'system'
                          ? { background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: C.gold, borderBottomLeftRadius: 4 }
                          : { background: 'var(--pd-card-bg)', border: `1px solid ${C.border}`, color: C.text, borderBottomLeftRadius: 4 }
                    }>
                    {msg.content}
                  </div>
                  {msg.suggestions?.length > 0 && (
                    <div className="space-y-1">
                      {msg.suggestions.slice(0, 3).map(p => (
                        <Link key={p.id} to={`/profile/${toProfileSlug(p.name, p.id)}`} onClick={() => setOpen(false)}
                          data-testid={`floating-suggestion-${p.id}`}
                          className="flex items-center gap-2 p-2 rounded-lg border transition-all hover:border-purple-500/20"
                          style={{ background: 'var(--pd-card-bg)', borderColor: C.border }}>
                          <img src={p.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name)}&background=8B5CF6&color=fff&size=32`}
                            alt="" className="w-7 h-7 rounded-md object-cover" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-semibold truncate" style={{ color: C.text }}>{p.name}</p>
                            <p className="text-[10px] truncate" style={{ color: C.purple }}>{p.podcast_name}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: '#8B5CF6' }}>
                  <Bot className="w-3 h-3 text-white" />
                </div>
                <div className="p-2.5 rounded-xl border flex items-center gap-2" style={{ background: 'var(--pd-card-bg)', borderColor: C.border }}>
                  <Loader2 className="w-3 h-3 animate-spin" style={{ color: C.purple }} />
                  <span className="text-xs" style={{ color: C.text2 }}>Finding matches...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Limit Reached Banner */}
          {isLimitReached && (
            <div className="mx-3 mb-2 p-3 rounded-xl text-center" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <Lock className="w-4 h-4 mx-auto mb-1.5" style={{ color: C.gold }} />
              <p className="text-xs font-medium mb-1" style={{ color: C.gold }}>Daily limit reached</p>
              <p className="text-[10px] mb-2" style={{ color: C.text2 }}>
                {usage.tier === 'free' || usage.tier === 'verified'
                  ? `Free plan: ${usage.limit} messages/day`
                  : `${TIER_LABELS[usage.tier]} plan: ${usage.limit} messages/day`
                }
              </p>
              <Link
                to="/pricing"
                onClick={() => setOpen(false)}
                data-testid="copilot-upgrade-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:opacity-90"
                style={{ background: '#8B5CF6' }}
              >
                <Zap className="w-3 h-3" /> Upgrade for more
              </Link>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t" style={{ borderColor: C.border }}>
            <form onSubmit={e => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={isLimitReached ? 'Daily limit reached...' : 'Ask about matches...'}
                disabled={loading || isLimitReached}
                data-testid="floating-copilot-input"
                className="flex-1 px-3 py-2 rounded-lg text-xs border-0 outline-none disabled:opacity-50"
                style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: C.text }}
              />
              <button type="submit" disabled={!input.trim() || loading || isLimitReached}
                data-testid="floating-copilot-send"
                className="px-3 py-2 rounded-lg transition-all disabled:opacity-30"
                style={{ background: input.trim() && !isLimitReached ? C.purple : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}>
                <Send className="w-3.5 h-3.5 text-white" />
              </button>
            </form>
            {/* Usage hint below input */}
            {usage && !usage.unlimited && !isLimitReached && (
              <p className="text-[9px] mt-1.5 text-center" style={{ color: C.muted }}>
                {usage.remaining} message{usage.remaining !== 1 ? 's' : ''} remaining today
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingCopilot;
